import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { deleteField, doc, getDoc, setDoc } from "firebase/firestore";

import CardSection from "../../../components/CardSection/CardSection";
import MyDatePicker from "../../../components/CardSection/MyDatePicker";
import { selectActiveUid } from "../../Auth/authSlice";
import { db } from "../../../config/firebaseConfig";
import { getKinshasaDateIso, isoToFrDmy } from "../../../utils/dateUtils";

const PUMPS = [
  "S1",
  "S2",
  "S3",
  "S4",
  "S5",
  "S6",
  "G1",
  "G2",
  "G3",
  "G4",
  "G5",
  "G6",
];

function parseIndexNumber(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return null;

  const normalized = rawValue.replace(/\s/g, "").replace(",", ".");
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function formatIndexNumber(value) {
  return value
    .toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\u202F|\u00A0/g, " ");
}

function isoToSlashDmy(isoDate) {
  return isoToFrDmy(isoDate).replace(/-/g, "/");
}

function previousIsoDate(isoDate) {
  const [year, month, day] = String(isoDate || "")
    .split("-")
    .map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() - 1);

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function entryTimeSeconds(key, value) {
  const canonicalMatch = String(key || "").match(/[SG]\d+_([DA])$/i);
  if (canonicalMatch) {
    return canonicalMatch[1].toUpperCase() === "D" ? 8 * 3600 : 20 * 3600;
  }

  const keyMatch = String(key || "").match(/[SG]\d+_(\d{2})_(\d{2})_(\d{2})$/i);
  if (keyMatch) {
    return (
      Number(keyMatch[1]) * 3600 +
      Number(keyMatch[2]) * 60 +
      Number(keyMatch[3])
    );
  }

  const dateMatch = String(value?.date || "").match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!dateMatch) return 0;
  return (
    Number(dateMatch[1]) * 3600 +
    Number(dateMatch[2]) * 60 +
    Number(dateMatch[3])
  );
}

function entryMoment(key, value) {
  const canonicalMatch = String(key || "").match(/[SG]\d+_([DA])$/i);
  if (canonicalMatch) return canonicalMatch[1].toUpperCase();

  const keyMatch = String(key || "").match(/[SG]\d+_(\d{2})_(\d{2})_(\d{2})$/i);
  if (keyMatch) return Number(keyMatch[1]) < 12 ? "D" : "A";

  const dateMatch = String(value?.date || "").match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!dateMatch) return null;
  return Number(dateMatch[1]) < 12 ? "D" : "A";
}

function findPreviousArrive(data, pump) {
  const entries = Object.entries(data || {})
    .filter(([, value]) => value?.type === "INDEX" && value.item === pump)
    .filter(([, value]) => value.value !== undefined && value.value !== null)
    .filter(([key, value]) => entryMoment(key, value) === "A")
    .map(([key, value]) => ({
      value: value.value,
      time: entryTimeSeconds(key, value),
    }))
    .sort((a, b) => b.time - a.time);

  if (entries.length === 0) return "";

  const parsed = parseIndexNumber(entries[0].value);
  return parsed === null ? String(entries[0].value) : formatIndexNumber(parsed);
}

function buildIndexPayload(rows, isoDate) {
  const slashDate = isoToSlashDmy(isoDate);
  return rows.reduce((payload, row) => {
    payload[`${row.pump}_D`] = {
      type: "INDEX",
      item: row.pump,
      value: row.depart,
      date: `${slashDate}, 08:00:00`,
    };

    if (row.arrive) {
      payload[`${row.pump}_A`] = {
        type: "INDEX",
        item: row.pump,
        value: row.arrive,
        date: `${slashDate}, 20:00:00`,
      };
    }

    return payload;
  }, {});
}

const IndexPump = () => {
  const activeUid = useSelector(selectActiveUid);
  const [cardOpen, setCardOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => getKinshasaDateIso());
  const [selectedPump, setSelectedPump] = useState("");
  const [depart, setDepart] = useState("");
  const [arrive, setArrive] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!activeUid || !selectedDate || !selectedPump) return undefined;

    let canceled = false;
    const loadPreviousArrive = async () => {
      try {
        const previousDate = previousIsoDate(selectedDate);
        if (!previousDate) return;

        const previousDocRef = doc(
          db,
          "users",
          activeUid,
          "data",
          isoToFrDmy(previousDate),
        );
        const previousSnap = await getDoc(previousDocRef);
        if (canceled) return;

        setDepart(
          previousSnap.exists()
            ? findPreviousArrive(previousSnap.data(), selectedPump)
            : "",
        );
      } catch (err) {
        console.error("Error loading previous index: ", err);
        if (!canceled) setDepart("");
      }
    };

    loadPreviousArrive();
    return () => {
      canceled = true;
    };
  }, [activeUid, selectedDate, selectedPump]);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
    setDepart("");
  };

  const handlePumpChange = (event) => {
    setSelectedPump(event.target.value);
    setDepart("");
    setError("");
  };

  const handleAdd = () => {
    setError("");
    const departNumber = parseIndexNumber(depart);
    const hasArrive = String(arrive || "").trim() !== "";
    const arriveNumber = parseIndexNumber(arrive);

    if (!selectedPump || departNumber === null) {
      setError("Completez la pompe et depart.");
      return;
    }

    if (hasArrive && arriveNumber === null) {
      setError("Arrive invalide.");
      return;
    }

    if (hasArrive && arriveNumber < departNumber) {
      setError("Depart ne peut pas etre superieur a arrive.");
      return;
    }

    const nextRow = {
      pump: selectedPump,
      depart: formatIndexNumber(departNumber),
      arrive: hasArrive ? formatIndexNumber(arriveNumber) : "",
      vente: hasArrive ? formatIndexNumber(arriveNumber - departNumber) : "",
    };

    setRows((currentRows) => [
      ...currentRows.filter((row) => row.pump !== selectedPump),
      nextRow,
    ]);
    setSelectedPump("");
    setDepart("");
    setArrive("");
  };

  const handleRemove = (pump) => {
    setRows((currentRows) => currentRows.filter((row) => row.pump !== pump));
  };

  const handleSend = async (event) => {
    event.stopPropagation();
    if (!activeUid || rows.length === 0 || !selectedDate) return;

    setLoading(true);
    setError("");

    try {
      const docId = isoToFrDmy(selectedDate);
      const docRef = doc(db, "users", activeUid, "data", docId);
      const snap = await getDoc(docRef);
      const momentsToReplace = rows.reduce((map, row) => {
        const moments = new Set(["D"]);
        if (row.arrive) moments.add("A");
        map.set(row.pump, moments);
        return map;
      }, new Map());
      const payload = {};

      if (snap.exists()) {
        for (const [key, value] of Object.entries(snap.data() || {})) {
          const moments = momentsToReplace.get(value?.item);
          const moment = entryMoment(key, value);
          if (value?.type === "INDEX" && moments?.has(moment)) {
            payload[key] = deleteField();
          }
        }
      }

      Object.assign(payload, buildIndexPayload(rows, selectedDate));
      await setDoc(docRef, payload, { merge: true });

      setRows([]);
      setSelectedPump("");
      setDepart("");
      setArrive("");
      setCardOpen(false);
    } catch (err) {
      console.error("Error adding index document: ", err);
      setError("Envoi impossible. Reessayez.");
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 4,
      }}
    >
      <CircularProgress color="primary" size={48} />
      <Typography sx={{ mt: 2 }} variant="body1">
        Chargement...
      </Typography>
    </Box>
  ) : (
    <CardSection
      title="INDEX"
      showDefaultControls={false}
      open={cardOpen}
      setOpen={setCardOpen}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <MyDatePicker value={selectedDate} onChange={handleDateChange} />

        <TextField
          select
          label="Pompe"
          value={selectedPump}
          onChange={handlePumpChange}
          size="small"
          fullWidth
        >
          {PUMPS.map((pump) => (
            <MenuItem key={pump} value={pump}>
              {pump}
            </MenuItem>
          ))}
        </TextField>

        <NumericFormat
          customInput={TextField}
          label="Depart"
          value={depart}
          thousandSeparator=" "
          decimalSeparator=","
          decimalScale={2}
          allowedDecimalSeparators={[",", "."]}
          inputMode="decimal"
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
        />

        <NumericFormat
          customInput={TextField}
          label="Arrive"
          value={arrive}
          onChange={(event) => setArrive(event.target.value)}
          thousandSeparator=" "
          decimalSeparator=","
          decimalScale={2}
          allowedDecimalSeparators={[",", "."]}
          inputMode="decimal"
          size="small"
          fullWidth
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleAdd}
          sx={{
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1,
            minHeight: 44,
            py: 0.5,
          }}
          fullWidth
        >
          +
        </Button>

        {rows.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="index table">
              <TableHead>
                <TableRow>
                  <TableCell>Pompe</TableCell>
                  <TableCell align="right">Depart</TableCell>
                  <TableCell align="right">Arrive</TableCell>
                  <TableCell align="right">Vente (L)</TableCell>
                  <TableCell padding="none" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.pump}>
                    <TableCell>{row.pump}</TableCell>
                    <TableCell align="right">{row.depart}</TableCell>
                    <TableCell align="right">{row.arrive}</TableCell>
                    <TableCell align="right">{row.vente}</TableCell>
                    <TableCell align="center" padding="none">
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(row.pump)}
                        sx={{ color: "error.main" }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={!activeUid || rows.length === 0 || !selectedDate}
          fullWidth
        >
          Envoyer
        </Button>
      </Box>
    </CardSection>
  );
};

export default IndexPump;
